import type { PushNudgeSurface } from "@/db/schema/push-nudge";

export type PushNudgeMockRow = {
  title: string;
  body: string;
  timestamp: string;
};

export type PushNudgeCopy = {
  headline: string;
  subhead: string;
  mockRows: PushNudgeMockRow[];
  primaryLabel: string;
  dismissLabel: string;
  /** States scope and exit. §5 requires both, and requires them to be true. */
  reassurance: string;
};

/**
 * Values a surface knows about the user's own situation.
 *
 * Every field is optional and every headline has a fallback, because a nudge
 * that renders "You're #undefined in line" is worse than one that renders a
 * plain sentence.
 */
export type PushNudgeParams = {
  firstName?: string | null;
  eventName?: string | null;
  dateLabel?: string | null;
  venueName?: string | null;
  waitlistPosition?: number | null;
  tierName?: string | null;
  sessionName?: string | null;
  dayLabel?: string | null;
  platformLabel?: string;
  /** The other side of a 1:1 chat, or the group's name. */
  counterpartName?: string | null;
  /** How many people are waiting on this host's approval. */
  pendingCount?: number | null;
  /** Formatted, already-localised money for the split surface, e.g. "₹450". */
  amountLabel?: string | null;
  /** Whether the split viewer is owed money rather than owing it. */
  isOwed?: boolean;
  /** What the referrer earns per accepted invite, formatted. */
  rewardLabel?: string | null;
};

/**
 * Whether this user has never been asked, or is already receiving quiet
 * notifications and is being offered the loud ones.
 *
 * The second case is a genuinely different conversation: they are not deciding
 * whether to hear from Hiive, they are deciding whether Hiive may interrupt
 * them. Pretending otherwise ("Turn on notifications" to someone already
 * getting them) reads as broken.
 */
export type PushNudgeMode = "first_ask" | "upgrade";

const EXIT = "Manage anytime in Settings.";

export function buildPushNudgeCopy(
  surface: PushNudgeSurface,
  mode: PushNudgeMode,
  params: PushNudgeParams = {},
): PushNudgeCopy {
  const primaryLabel =
    mode === "upgrade" ? "Turn on alerts" : "Turn on notifications";
  const dismissLabel = "Not now";
  const event = params.eventName?.trim() || "this event";
  const tier = params.tierName?.trim() || "the organiser";
  const session = params.sessionName?.trim() || "this session";
  const counterpart = params.counterpartName?.trim() || "They";

  switch (surface) {
    case "event_waitlist_joined": {
      const position = params.waitlistPosition;
      return {
        headline:
          typeof position === "number" && position > 0
            ? `You're #${position} in line.`
            : "You're on the waitlist.",
        subhead:
          mode === "upgrade"
            ? "A spot can open at any hour. A quiet notification is easy to miss."
            : "A spot can open at any hour — we can only tell you if alerts are on.",
        mockRows: [
          {
            title: event,
            body: "A spot just opened. First to book gets it.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only about this waitlist. ${EXIT}`,
      };
    }

    case "tier_join_request_sent":
      return {
        headline: "Your request is in.",
        subhead:
          mode === "upgrade"
            ? `${tier} decides in their own time — be told the moment they do.`
            : `${tier} decides in their own time. We'll tell you the moment they do.`,
        mockRows: [
          {
            title: tier,
            body: "You're in — your request was approved.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only about this request. ${EXIT}`,
      };

    case "event_booking_success":
      return {
        headline: params.firstName
          ? `You're in, ${params.firstName}.`
          : "You're in.",
        subhead: `${[event, params.dateLabel, params.venueName]
          .filter(Boolean)
          .join(
            " · ",
          )}. Get an alert if plans change or the event is cancelled.`,
        mockRows: [
          {
            title: event,
            body: "Starts in 30 minutes — have your QR ready.",
            timestamp: "now",
          },
          {
            title: event,
            // Deliberately a TIME change, not a venue one. The subhead above
            // already names the venue this booking is at, so a "moved to
            // {venueName}" row announced a move to the place it was already
            // being held — and the only other venue name available here is that
            // same one. A time change is the one kind of change this surface can
            // illustrate truthfully without inventing a second location.
            body: "Start time moved to 7:00 PM — same place.",
            timestamp: "2m",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Reminders, changes and cancellations for your bookings. ${EXIT}`,
      };

    case "activity_session_joined":
      return {
        headline: params.dayLabel
          ? `You're playing ${params.dayLabel}.`
          : "You're in the session.",
        subhead:
          mode === "upgrade"
            ? `Time and venue changes for ${session} land louder.`
            : `Time and venue changes for ${session}, before you leave the house.`,
        mockRows: [
          {
            title: session,
            body: params.venueName
              ? `Venue moved to ${params.venueName}.`
              : "The venue has changed.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only sessions you've joined. ${EXIT}`,
      };

    case "chat_message_sent":
      return {
        headline: "Sent.",
        subhead:
          mode === "upgrade"
            ? "A reply can land at any hour. A quiet notification is easy to miss."
            : "A reply can land at any hour — we can only tell you if alerts are on.",
        mockRows: [
          {
            title: params.counterpartName?.trim() || "New message",
            body: "Replied to your message.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only messages and mentions in your chats. ${EXIT}`,
      };

    case "connection_request_sent":
      return {
        headline: "Your request is in.",
        subhead:
          mode === "upgrade"
            ? `${counterpart} answers in their own time — be told the moment they do.`
            : `${counterpart} answers in their own time. We'll tell you the moment they do.`,
        mockRows: [
          {
            title: params.counterpartName?.trim() || "New connection",
            body: "Accepted your request — you can message now.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only about requests you've sent. ${EXIT}`,
      };

    case "activity_session_hosted": {
      const pending = params.pendingCount ?? 0;
      return {
        // States their obligation rather than our want: somebody is standing
        // still until this person taps something.
        headline:
          pending > 0
            ? `${pending} ${pending === 1 ? "person is" : "people are"} waiting on you.`
            : "You're hosting.",
        subhead:
          mode === "upgrade"
            ? `Join requests for ${session} land louder, so nobody waits on a tab you forgot.`
            : `You only saw these because you opened the app. We can tell you instead.`,
        mockRows: [
          {
            title: session,
            body: "Someone asked to join — approve or decline.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only sessions you host. ${EXIT}`,
      };
    }

    case "session_split_open": {
      const amount = params.amountLabel?.trim();
      return {
        headline: params.isOwed
          ? amount
            ? `You're owed ${amount}.`
            : "You're owed money on this session."
          : amount
            ? `You owe ${amount}.`
            : "This session's split is still open.",
        subhead:
          mode === "upgrade"
            ? "Payments and reminders on this split land louder."
            : "Splits settle between people, not on a schedule — alerts are how you find out it moved.",
        mockRows: [
          {
            title: session,
            // Written from the side that would receive it: an organizer hears
            // that somebody paid THEM, a player hears that they still owe.
            body: params.isOwed
              ? "A share was just paid — the balance has moved."
              : "Reminder: your share is still outstanding.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only splits you're part of. ${EXIT}`,
      };
    }

    case "referral_link_shared": {
      const reward = params.rewardLabel?.trim();
      return {
        headline: "Invite sent.",
        subhead: reward
          ? `${reward} is added to your wallet automatically when your friend qualifies.`
          : "Your reward is added to your wallet automatically when your friend qualifies.",
        mockRows: [
          {
            title: "Hiive Money",
            body: reward
              ? `${reward} will be added when earned.`
              : "A reward will be added when earned.",
            timestamp: "now",
          },
        ],
        primaryLabel,
        dismissLabel,
        reassurance: `Only about your invites and rewards. ${EXIT}`,
      };
    }

    case "push_rescue": {
      const platform = params.platformLabel ?? "your";
      return {
        // Names what they lost rather than what we want, per §5 — and it is the
        // literal truth: these notifications were being sent and were dropped.
        headline: "Hiive alerts are switched off.",
        subhead: `Waitlist spots and session changes aren't reaching you. Turn them back on in ${platform} Settings.`,
        mockRows: [],
        primaryLabel: "Open Settings",
        dismissLabel: "Not now",
        reassurance: EXIT,
      };
    }
  }
}

/**
 * The post-grant line. Reads from what the user will actually receive rather
 * than a fixed list, so it cannot drift from the preferences that gate delivery.
 */
export function buildPushNudgeConfirmation(scopes: string[]): {
  headline: string;
  subhead: string;
} {
  const listed =
    scopes.length === 0
      ? "the things you've joined"
      : scopes.length === 1
        ? scopes[0]
        : `${scopes.slice(0, -1).join(", ")} and ${scopes[scopes.length - 1]}`;

  return {
    headline: "You're set.",
    subhead: `You'll hear about ${listed}. Change what reaches you anytime in Settings.`,
  };
}
